import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ShortDetailPage() {
  const { id } = useParams();
  const [short, setShort] = useState(null);
  const [parentContent, setParentContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShort = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/shorts/${id}`);
        const shortData = response.data;
        setShort(shortData);

        // Fetch parent content
        if (shortData.parentType === 'article') {
          const articleRes = await axios.get(`${API_URL}/api/articles?pageSize=100`);
          const article = articleRes.data.find(a => a.id === shortData.parentId);
          setParentContent(article);
        } else if (shortData.parentType === 'video') {
          const videoRes = await axios.get(`${API_URL}/api/videos?pageSize=100`);
          const video = videoRes.data.find(v => v.id === shortData.parentId);
          setParentContent(video);
        }
      } catch (error) {
        console.error('Error fetching short:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchShort();
    }
  }, [id]);

  if (loading) {
    return (
      <>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading short...</p>
          </div>
        </div>
      </>
    );
  }

  if (!short) {
    return (
      <>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold mb-2">Short not found</h2>
            <Link to="/shorts" className="text-primary hover:underline">Return to shorts</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const parentUrl = short.parentType === 'article' 
    ? `/articles/${parentContent?.slug}` 
    : `/videos/${parentContent?.slug}`;

  return (
    <>
      <Helmet>
        <title>Short · B&B Magazine</title>
        <meta name="description" content={short.summary} />
      </Helmet>

      <HeaderNav />

      <main>
        <section className="w-full py-12 md:py-16">
          <div className="container max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back button */}
            <Link 
              to="/shorts"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200 mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Shorts
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Short Content */}
              <Card className="bg-[hsl(var(--surface-2))] border-border p-6 md:p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Badge className="px-2.5 py-1 rounded-full text-xs uppercase tracking-wide bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.3)]">
                    Quick Take
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {short.parentType}
                  </Badge>
                </div>

                {/* Summary */}
                <div className="prose prose-invert max-w-none mb-6">
                  <p className="text-lg md:text-xl leading-relaxed text-foreground">
                    {short.summary}
                  </p>
                </div>

                {/* Tags */}
                {short.tags && short.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {short.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="font-ui text-xs">
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                )}

                <Separator className="my-6" />

                {/* Parent Content Link */}
                {parentContent && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-4">
                      This is a summary of the full {short.parentType}:
                    </p>
                    <Link to={parentUrl}>
                      <Card className="bg-[hsl(var(--surface-3))] border-border p-5 hover:border-primary/30 transition-all duration-200 group">
                        <div className="flex items-start gap-4">
                          {(short.thumbnailUrl || parentContent.heroImageUrl || parentContent.thumbnailUrl) && (
                            <img
                              src={short.thumbnailUrl || parentContent.heroImageUrl || parentContent.thumbnailUrl}
                              alt=""
                              className="w-24 h-24 md:w-32 md:h-32 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <h3 className="font-display text-xl font-semibold mb-2 group-hover:text-primary transition-colors duration-200">
                              {parentContent.title}
                            </h3>
                            {parentContent.dek && (
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                {parentContent.dek}
                              </p>
                            )}
                            <div className="flex items-center gap-2 text-primary text-sm font-ui font-medium">
                              <span>Read full {short.parentType}</span>
                              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </div>
                )}
              </Card>

              {/* CTA to browse more */}
              <div className="mt-8 text-center">
                <Link to="/shorts">
                  <Button variant="outline" className="font-ui">
                    Browse More Shorts
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
